import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, SCHOOLS } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeft, Save, Archive, Send, Edit3, Copy } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';

// ----------------------------------------------------------------------
// LIST view  (/admin/builder/programs)
// ----------------------------------------------------------------------
export function ProgramListPage() {
  const [rows, setRows] = useState([]);
  const load = () => api.get('/admin/builder/programs').then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const archive = async (id) => {
    await api.post(`/admin/builder/programs/${id}/archive`);
    toast.success('Program archived');
    load();
  };
  const publish = async (id) => {
    await api.post(`/admin/builder/programs/${id}/publish`);
    toast.success('Program published');
    load();
  };

  return (
    <div data-testid="admin-program-list">
      <div className="bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-gold font-semibold">Builders</div>
          <h1 className="font-display font-semibold text-2xl text-navy mt-1">Program Builder</h1>
        </div>
        <Link to="/admin/builder/programs/new" data-testid="new-program-btn"><Button className="btn-gold"><Plus className="h-4 w-4 mr-1" /> New Program</Button></Link>
      </div>
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                {['Title','School','Price','Level','Status','Actions'].map(h => <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.program_id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-navy">{r.title}</div>
                    <div className="text-xs text-slate-400 font-mono">{r.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.school}</td>
                  <td className="px-4 py-3 text-navy font-serif">${(r.price_usd || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-slate-700">{r.level}</td>
                  <td className="px-4 py-3">
                    {r.is_coming_soon ? <Badge color="gold">Coming Soon</Badge> :
                      r.is_archived ? <Badge color="slate">Archived</Badge> :
                      r.is_published ? <Badge color="green">Published</Badge> :
                      <Badge color="slate">Draft</Badge>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Link to={`/admin/builder/programs/${r.program_id}`}><Button size="sm" variant="outline" data-testid={`edit-program-${r.program_id}`}><Edit3 className="h-3.5 w-3.5" /></Button></Link>
                      <CloneProgramDialog program={r} onDone={load} />
                      {r.is_published
                        ? <Button size="sm" variant="outline" onClick={() => archive(r.program_id)} data-testid={`archive-program-${r.program_id}`}><Archive className="h-3.5 w-3.5" /></Button>
                        : <Button size="sm" className="btn-gold" onClick={() => publish(r.program_id)} data-testid={`publish-program-${r.program_id}`}><Send className="h-3.5 w-3.5" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-12">No programs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Badge({ children, color }) {
  const map = {
    green: 'bg-trjgreen/10 text-trjgreen',
    gold: 'bg-gold/15 text-gold',
    slate: 'bg-slate-100 text-slate-600',
  };
  return <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded ${map[color] || map.slate}`}>{children}</span>;
}

function CloneProgramDialog({ program, onDone }) {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSlug(`${program.slug}-${new Date().getFullYear() + 1}`);
      setTitle(`${program.title} — ${new Date().getFullYear() + 1} Edition`);
    }
  }, [open, program]);

  const clone = async () => {
    if (!slug || !title) { toast.error('Slug and title required'); return; }
    setBusy(true);
    try {
      await api.post(`/admin/builder/programs/${program.program_id}/clone`,
        { new_slug: slug, new_title: title, include_courses: true });
      toast.success('Program cloned as a draft');
      setOpen(false); onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clone failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`clone-program-${program.program_id}`}><Copy className="h-3.5 w-3.5" /></Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Clone "{program.title}"</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">A draft copy will be created with all modules, lessons, and quizzes. Perfect for new year editions, CE refreshers, or A/B variants.</p>
        <div className="space-y-3 mt-2">
          <div><Label>New slug</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="clone-slug" /></div>
          <div><Label>New title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="clone-title" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="btn-navy" onClick={clone} disabled={busy} data-testid="clone-confirm"><Copy className="h-4 w-4 mr-2" /> {busy ? 'Cloning…' : 'Clone'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------
// EDIT/CREATE  /admin/builder/programs/:id  ("new" or program_id)
// ----------------------------------------------------------------------
export function ProgramBuilderPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState({
    slug: '', title: '', school: SCHOOLS[0].name, short_description: '', long_description: '',
    price_usd: 0, duration_hours: 0, level: 'Beginner', image_url: '',
    is_published: true, is_coming_soon: false,
    outcomes: [], syllabus: [], course_ids: [],
  });
  const [programId, setProgramId] = useState(null);

  useEffect(() => {
    if (isNew) return;
    api.get('/admin/builder/programs').then(({ data }) => {
      const p = data.find(x => x.program_id === id);
      if (p) {
        setForm({
          slug: p.slug, title: p.title, school: p.school,
          short_description: p.short_description || '', long_description: p.long_description || '',
          price_usd: p.price_usd, duration_hours: p.duration_hours,
          level: p.level, image_url: p.image_url || '',
          is_published: p.is_published, is_coming_soon: p.is_coming_soon,
          outcomes: p.outcomes || [], syllabus: p.syllabus || [], course_ids: p.course_ids || [],
        });
        setProgramId(p.program_id);
      }
    });
  }, [id, isNew]);

  const save = async () => {
    try {
      if (isNew) {
        const { data } = await api.post('/admin/builder/programs', form);
        toast.success('Program created');
        navigate(`/admin/builder/programs/${data.program_id}`);
      } else {
        await api.put(`/admin/builder/programs/${programId}`, form);
        toast.success('Saved');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const updateField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateOutcome = (i, v) => setForm(f => ({ ...f, outcomes: f.outcomes.map((o, idx) => idx === i ? v : o) }));
  const addOutcome = () => setForm(f => ({ ...f, outcomes: [...f.outcomes, ''] }));
  const removeOutcome = (i) => setForm(f => ({ ...f, outcomes: f.outcomes.filter((_, idx) => idx !== i) }));
  const updateSyllabus = (i, k, v) => setForm(f => ({ ...f, syllabus: f.syllabus.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const addSyllabus = () => setForm(f => ({ ...f, syllabus: [...f.syllabus, { title: '', duration: '' }] }));
  const removeSyllabus = (i) => setForm(f => ({ ...f, syllabus: f.syllabus.filter((_, idx) => idx !== i) }));

  return (
    <div data-testid="program-builder">
      <div className="bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/builder/programs" className="text-slate-500 hover:text-navy"><ArrowLeft className="h-5 w-5" /></Link>
          <div>
            <div className="text-xs uppercase tracking-widest text-gold font-semibold">{isNew ? 'New Program' : 'Edit Program'}</div>
            <h1 className="font-display font-semibold text-2xl text-navy mt-1">{form.title || 'Untitled program'}</h1>
          </div>
        </div>
        <Button onClick={save} className="btn-navy" data-testid="save-program-btn"><Save className="h-4 w-4 mr-2" /> Save</Button>
      </div>
      <div className="p-8 space-y-6 max-w-5xl">
        <Section title="Basics">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Slug (URL)"><Input data-testid="pb-slug" value={form.slug} onChange={(e) => updateField('slug', e.target.value)} placeholder="my-new-program" /></Field>
            <Field label="Title"><Input data-testid="pb-title" value={form.title} onChange={(e) => updateField('title', e.target.value)} /></Field>
            <Field label="School">
              <Select value={form.school} onValueChange={(v) => updateField('school', v)}>
                <SelectTrigger data-testid="pb-school"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCHOOLS.map(s => <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Level">
              <Select value={form.level} onValueChange={(v) => updateField('level', v)}>
                <SelectTrigger data-testid="pb-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Beginner','Intermediate','Advanced','All Levels'].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Tuition (USD)"><Input type="number" step="0.01" data-testid="pb-price" value={form.price_usd} onChange={(e) => updateField('price_usd', parseFloat(e.target.value) || 0)} /></Field>
            <Field label="Duration (hours)"><Input type="number" data-testid="pb-hours" value={form.duration_hours} onChange={(e) => updateField('duration_hours', parseInt(e.target.value) || 0)} /></Field>
            <Field label="Image URL" className="md:col-span-2"><Input data-testid="pb-image" value={form.image_url} onChange={(e) => updateField('image_url', e.target.value)} placeholder="https://…" /></Field>
            <Field label="Short description" className="md:col-span-2"><Textarea rows={2} data-testid="pb-short" value={form.short_description} onChange={(e) => updateField('short_description', e.target.value)} /></Field>
            <Field label="Long description" className="md:col-span-2"><Textarea rows={5} data-testid="pb-long" value={form.long_description} onChange={(e) => updateField('long_description', e.target.value)} /></Field>
          </div>
          <div className="flex gap-8 mt-4">
            <Toggle label="Published" checked={form.is_published} onChange={(v) => updateField('is_published', v)} testid="pb-toggle-publish" />
            <Toggle label="Coming Soon (waitlist)" checked={form.is_coming_soon} onChange={(v) => updateField('is_coming_soon', v)} testid="pb-toggle-coming-soon" />
          </div>
        </Section>

        <Section title="Learning outcomes" action={<Button size="sm" variant="outline" onClick={addOutcome} data-testid="add-outcome-btn"><Plus className="h-3 w-3 mr-1" /> Add</Button>}>
          <div className="space-y-2">
            {form.outcomes.map((o, i) => (
              <div key={i} className="flex gap-2">
                <Input value={o} onChange={(e) => updateOutcome(i, e.target.value)} placeholder="Outcome statement" />
                <Button size="sm" variant="outline" onClick={() => removeOutcome(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
            {form.outcomes.length === 0 && <div className="text-sm text-slate-500">No outcomes added yet.</div>}
          </div>
        </Section>

        <Section title="Curriculum / Syllabus" action={<Button size="sm" variant="outline" onClick={addSyllabus} data-testid="add-syllabus-btn"><Plus className="h-3 w-3 mr-1" /> Add Module</Button>}>
          <div className="space-y-2">
            {form.syllabus.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <Input className="col-span-8" value={s.title || ''} onChange={(e) => updateSyllabus(i, 'title', e.target.value)} placeholder="Module title" />
                <Input className="col-span-3" value={s.duration || ''} onChange={(e) => updateSyllabus(i, 'duration', e.target.value)} placeholder="e.g. 6 hrs" />
                <Button size="sm" variant="outline" onClick={() => removeSyllabus(i)} className="col-span-1"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-navy">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
function Field({ label, children, className = '' }) {
  return <div className={className}><Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>;
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
      <span className="text-sm text-navy">{label}</span>
    </label>
  );
}
